import type { WorkflowDefinitionDocument } from './platform-workflow-definition-lifecycle.models';
import { projectWorkflowDraftGraph } from './workflow-draft-graph-projection';
import type {
  WorkflowReadOnlyGraph,
  WorkflowReadOnlyGraphFact,
  WorkflowReadOnlyGraphSelection,
} from './workflow-read-only-graph.models';

/** Exact declared path inspection, independent of editor buffers and runtime state. */
export interface WorkflowRouteInspection {
  readonly selection: WorkflowReadOnlyGraphSelection;
  readonly title: string;
  readonly sourceLabel: string;
  readonly destinationLabel: string;
  readonly resultLabel: string;
  readonly movementLabel: string;
  readonly description: string;
  readonly completionNote: string;
  readonly facts: readonly WorkflowReadOnlyGraphFact[];
  readonly declaration: string | null;
  readonly canOpenEditor: boolean;
  readonly disabledReason: string | null;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/** A structural reading only; server contracts and per-visit checks remain authoritative. */
function hasForwardPath(
  document: WorkflowDefinitionDocument,
  graph: WorkflowReadOnlyGraph,
  fromNodeId: string,
  toNodeId: string,
): boolean {
  const visited = new Set<string>();
  const pending = [fromNodeId];
  while (pending.length) {
    const nodeId = pending.pop()!;
    if (nodeId === toNodeId) return true;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);
    for (const path of graph.edges) {
      const raw: unknown = document.edges[path.sourceIndex];
      if (
        !path.isInvalid &&
        record(raw) &&
        raw['pathKindCode'] == null &&
        path.fromSourceNodeId === nodeId
      )
        pending.push(path.toSourceNodeId);
    }
  }
  return false;
}

/** Rechecks all three authored/renderer identity witnesses without choosing another edge. */
export function workflowRouteInspection(
  document: WorkflowDefinitionDocument | null,
  selection: WorkflowReadOnlyGraphSelection | null,
): WorkflowRouteInspection | null {
  if (!document || selection?.kind !== 'edge') return null;
  const graph = projectWorkflowDraftGraph(document);
  const edge = graph.edges.find(
    (candidate) =>
      candidate.id === selection.rendererId &&
      candidate.sourceId === selection.sourceId &&
      candidate.sourceIndex === selection.sourceIndex,
  );
  if (!edge)
    return {
      selection: { ...selection },
      title: 'Route changed',
      sourceLabel: 'Retained selection',
      destinationLabel: 'Select the current route on the map',
      resultLabel: 'Unavailable for this selection',
      movementLabel: 'Not resolved',
      description:
        'The selected route no longer matches its exact position and identity. Select it again before opening an editor.',
      completionNote: 'The working document has not been changed.',
      facts: [{ label: 'Retained route ID', value: selection.sourceId }],
      declaration: null,
      canOpenEditor: false,
      disabledReason: 'Select the current route on the map before editing.',
    };

  const raw: unknown = document.edges[edge.sourceIndex];
  const readable =
    record(raw) &&
    typeof raw['id'] === 'string' &&
    !!raw['id'].trim() &&
    raw['id'] === edge.sourceId &&
    typeof raw['fromNodeId'] === 'string' &&
    typeof raw['toNodeId'] === 'string' &&
    (raw['outcomeCode'] == null || typeof raw['outcomeCode'] === 'string') &&
    (raw['pathKindCode'] == null || typeof raw['pathKindCode'] === 'string');
  const duplicateIdentity =
    graph.edges.filter((candidate) => candidate.sourceId === edge.sourceId)
      .length !== 1;
  const disabledReason = !readable
    ? 'This saved route has incomplete or unsupported identity or selector fields. Its declaration is retained for inspection.'
    : duplicateIdentity
      ? 'More than one route uses this stable identity. Resolve the duplicate identity before editing.'
      : !selection.isAuthorable
        ? 'This exact route is available for inspection only in the current context.'
        : null;
  const pathKind = edge.pathKindCode;
  const source = graph.nodes.find((node) => node.id === edge.source);
  const destination = graph.nodes.find((node) => node.id === edge.target);
  const supportedReturnShape =
    readable &&
    !edge.isInvalid &&
    source?.kind === 'routing-decision' &&
    destination?.kind === 'human-step' &&
    hasForwardPath(document, graph, edge.toSourceNodeId, edge.fromSourceNodeId);
  const unsupportedReturn = pathKind === 'return' && !supportedReturnShape;
  const movementLabel = !readable
    ? 'Retained declaration needs inspection'
    : unsupportedReturn
      ? 'Retained return path · needs inspection'
      : pathKind === 'return'
        ? 'Return visit'
        : pathKind == null || pathKind === ''
          ? 'Continue'
          : `Retained path behavior: ${pathKind}`;
  const description = !readable
    ? 'Some retained fields cannot be interpreted safely. The exact selected declaration remains available below.'
    : unsupportedReturn
      ? 'This route retains a return declaration whose source, destination or forward-path context is not compatible with a supported return. Inspect the exact declaration; no runtime return behavior is inferred.'
      : pathKind === 'return'
        ? `This configured return can lead from “${edge.fromDisplayValue}” to “${edge.toDisplayValue}” for a fresh visit after completion. Earlier visit history is retained and required evidence is collected again. Server validation and runtime visit checks still apply.`
        : pathKind == null || pathKind === ''
          ? `This declared path continues from “${edge.fromDisplayValue}” to “${edge.toDisplayValue}”.`
          : 'This route retains an unfamiliar path behavior. Inspect its exact declaration and current compatibility before changing it.';
  const completionNote =
    source?.kind === 'human-step'
      ? 'Complete step checks requirements before following the configured route. Saving a form or recording a result leaves the step open.'
      : unsupportedReturn
        ? 'The exact saved path is retained. Current compatibility must be checked before this route can be used.'
        : source?.kind === 'automated-step'
          ? 'The activity result selects its declared route. Worker execution and current authority remain separate from these saved settings.'
          : source?.kind === 'routing-decision'
            ? 'This path belongs to an authored routing decision. Inspect its condition and other declared paths; it is not a separate completion action.'
            : 'This is a declared path, not evidence that a workflow has taken it. Validation and runtime state remain separate.';
  return {
    selection: { ...selection },
    title: 'Route',
    sourceLabel: edge.fromDisplayValue,
    destinationLabel: edge.toDisplayValue,
    resultLabel:
      record(raw) &&
      raw['outcomeCode'] != null &&
      typeof raw['outcomeCode'] !== 'string'
        ? 'Retained result selector needs inspection'
        : edge.outcomeCode || 'No result selector',
    movementLabel,
    description,
    completionNote,
    facts: edge.detail.facts.map((fact) => ({ ...fact })),
    declaration: JSON.stringify(raw, null, 2) ?? null,
    canOpenEditor: disabledReason === null,
    disabledReason,
  };
}
